import { useRef, useCallback } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Home, Users, Sparkles, CalendarDays, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'

const navItems = [
  { to: '/', icon: Home, labelKey: 'nav.home' },
  { to: '/contacts', icon: Users, labelKey: 'nav.people' },
  { to: '/reminders', icon: CalendarDays, labelKey: 'nav.events' },
  { to: '/settings', icon: MoreHorizontal, labelKey: 'nav.more' },
] as const

interface BottomNavProps {
  onAiTap?: () => void
  onAiLongPress?: () => void
  visible?: boolean
}

const LONG_PRESS_MS = 500

export function BottomNav({ onAiTap, onAiLongPress, visible = true }: BottomNavProps) {
  const { t } = useTranslation()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const didLongPressRef = useRef(false)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const handlePointerDown = useCallback(() => {
    didLongPressRef.current = false
    timerRef.current = setTimeout(() => {
      didLongPressRef.current = true
      onAiLongPress?.()
    }, LONG_PRESS_MS)
  }, [onAiLongPress])

  const handlePointerUp = useCallback(() => {
    clearTimer()
    if (!didLongPressRef.current) {
      onAiTap?.()
    }
  }, [clearTimer, onAiTap])

  const handlePointerCancel = useCallback(() => {
    clearTimer()
  }, [clearTimer])

  const renderNavLink = ({ to, icon: Icon, labelKey }: (typeof navItems)[number]) => (
    <NavLink
      key={to}
      to={to}
      className={({ isActive }) =>
        cn(
          'flex flex-col items-center gap-0.5 text-[10px] font-semibold tracking-wide transition-all',
          isActive
            ? 'text-primary dark:text-primary-foreground scale-110'
            : 'text-muted-foreground/60 hover:text-foreground/80',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className="h-5 w-5" strokeWidth={isActive ? 2.5 : 2} />
          <span>{t(labelKey)}</span>
        </>
      )}
    </NavLink>
  )

  return (
    <nav
      className={cn(
        'glass-nav border-primary/10 fixed right-0 bottom-0 left-0 z-40 flex flex-col rounded-t-3xl border-t shadow-[0_-8px_30px_rgb(0,0,0,0.12)] transition-transform duration-200 ease-out',
        visible ? 'translate-y-0' : 'translate-y-full',
      )}
    >
      <div className="flex h-16 items-center justify-around pt-1">
        {navItems.slice(0, 2).map(renderNavLink)}

        {/* Center AI assistant button */}
        <button
          data-ai-button
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onContextMenu={(e) => e.preventDefault()}
          className="relative -mt-6 flex touch-none flex-col items-center gap-1 text-[10px] font-semibold tracking-wide select-none"
        >
          <div className="relative">
            <div className="bg-ring/25 absolute inset-0 rounded-full blur-xl" />
            <div className="bg-primary border-background relative flex h-16 w-16 items-center justify-center rounded-full border-4 shadow-2xl">
              <Sparkles className="text-primary-foreground h-7 w-7" />
            </div>
          </div>
          <span className="text-primary font-semibold">{t('nav.askAi', 'Ask AI')}</span>
        </button>

        {navItems.slice(2).map(renderNavLink)}
      </div>
      {/* Physical spacer to fill the iOS safe-area / home-indicator gap */}
      <div className="safe-area-bottom-spacer" />
    </nav>
  )
}
